import Highcharts, { Options } from 'highcharts';
import { PieChartType } from 'src/api/api-types/diagrams';
import { mapColorGuid } from '../../pages/Generators/helpers/common';
import {
  contextButtonTheme,
  PIE_CHART_SIZE_DEFAULT,
  CHART_HEIGHT,
  pieChartLegendConfig,
  pieChartResponsiveConfig,
  chartLoadingConfig,
  chartTitleStyle,
  pieDataLabelsStyle,
} from '../../pages/Dashboard/components/Charts/config/constant';
import { ClickOnLegendType, ExportCallBackType } from '../../types';
import { exportOptions } from '../../pages/Dashboard/helpers/common';
import {
  PrintableChart,
  downloadPdf,
  getPieChartExportRasterImageProps,
  getPieChartExportSVGProps,
  formatPieDataLabel,
  applyMinSliceAngle,
} from './utils';
import { ExportFileType } from '../../api/exportTypes';
import HighchartsReact from 'highcharts-react-official';

export class PolarChart extends PrintableChart {
  protected clickOnSection?: ClickOnLegendType;
  protected exportCallBack?: ExportCallBackType;
  public options: Options;
  public chartRef?: HighchartsReact.Props['pie'];

  constructor(
    polarChart: PieChartType,
    callBackClick: ClickOnLegendType,
    exportCallBack: ExportCallBackType,
  ) {
    const options = getPieChartExportRasterImageProps('application/pdf');
    super(
      options.exportOptions.sourceWidth,
      options.exportOptions.sourceHeight,
      options.exportOptions.scale,
    );

    this.clickOnSection = callBackClick;
    this.exportCallBack = exportCallBack;
    this.options = {
      chart: {
        type: 'pie',
        height: CHART_HEIGHT,
      },
      loading: chartLoadingConfig,
      title: {
        text: polarChart.title,
        align: 'left',
        y: 24,
        widthAdjust: -100,
        style: chartTitleStyle,
      },
      series: [
        {
          type: 'pie',
          name: polarChart.title,
          data: this.formatToSeries(polarChart),
          dataLabels: {
            enabled: true,
            useHTML: true,
            formatter: function (this: Highcharts.PointLabelObject) {
              const originalY = (this.point as any).originalY;
              return formatPieDataLabel(
                this.point.name,
                this.point.y,
                originalY,
              );
            },
            style: pieDataLabelsStyle,
          },
          tooltip: {
            pointFormatter: function () {
              const originalY = (this as any).originalY;
              const displayValue = originalY !== undefined ? originalY : this.y;
              return `${(this as any).i}: ${displayValue}`;
            },
          },
        },
      ] as Highcharts.SeriesOptionsType[],
      plotOptions: {
        pie: {
          size: PIE_CHART_SIZE_DEFAULT,
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            enabled: true,
            useHTML: true,
            formatter: function (this: Highcharts.PointLabelObject) {
              const originalY = (this.point as any).originalY;
              return formatPieDataLabel(
                this.point.name,
                this.point.y,
                originalY,
              );
            },
          },
        },
        series: {
          showInNavigator: true,
          point: {
            events: {
              click: data => {
                if (!this.clickOnSection) return;
                const isDeselect = data.point.selected;
                const originalY = (data.point as any).originalY;
                this.clickOnSection(
                  (data.point as Highcharts.Point & { id: string }).id,
                  isDeselect ? null : data.point.name,
                  isDeselect ? null : data.point.series.name,
                  isDeselect ? null : originalY ?? data.point.y,
                );
              },
            },
          },
        },
      },
      legend: pieChartLegendConfig,
      responsive: pieChartResponsiveConfig,
      exporting: {
        chartOptions: {
          plotOptions: {
            series: {
              dataLabels: {
                enabled: true,
              },
            },
          },
        },
        buttons: {
          contextButton: {
            ...contextButtonTheme,
            menuItems: [
              'viewFullscreen',
              'printChart',
              'separator',
              'downloadPNG',
              'downloadJPEG',
              'downloadPDF',
              'downloadSVG',
              'separator',
              'downloadCSV',
              'downloadXSLS',
            ],
          },
        },
        menuItemDefinitions: {
          downloadXSLS: {
            onclick: () => {
              if (!this.exportCallBack) return;
              this.chartRef.fullscreen.isOpen && this.chartRef.showLoading();
              this.exportCallBack({
                dashboard: [
                  exportOptions[
                    "SLCP KPI's Not Compliant by Material Type Pie Chart"
                  ],
                ],
                type: ExportFileType.XLSX,
              });
            },
            text: 'Download XSLS',
          },
          downloadSVG: {
            onclick: () => {
              if (!this.chartRef) return;
              const options = getPieChartExportSVGProps('image/svg+xml');
              this.chartRef.exportChartLocal(
                options.exportOptions,
                options.chartOptions,
              );
            },
          },
          downloadCSV: {
            onclick: () => {
              if (!this.exportCallBack) return;
              this.chartRef.fullscreen.isOpen && this.chartRef.showLoading();
              this.exportCallBack({
                dashboard: [
                  exportOptions[
                    "SLCP KPI's Not Compliant by Material Type Pie Chart"
                  ],
                ],
                type: ExportFileType.CSV,
              });
            },
            text: 'Download CSV',
          },
          downloadPNG: {
            onclick: () => {
              if (!this.chartRef) return;
              const options = getPieChartExportRasterImageProps('image/png');
              this.chartRef.exportChartLocal(
                options.exportOptions,
                options.chartOptions,
              );
            },
            text: 'Download PNG image',
          },
          downloadJPEG: {
            onclick: () => {
              if (!this.chartRef) return;
              const options = getPieChartExportRasterImageProps('image/jpeg');
              this.chartRef.exportChartLocal(
                options.exportOptions,
                options.chartOptions,
              );
            },
            text: 'Download JPEG image',
          },
          printChart: {
            onclick: () => {
              if (!this.chartRef || !this.onPrint) return;
              this.onPrint();
            },
            text: 'Print chart',
          },
          downloadPDF: {
            onclick: () => {
              if (!this.chartRef) return;
              const options =
                getPieChartExportRasterImageProps('application/pdf');
              downloadPdf(
                this.chartRef,
                options.exportOptions.sourceWidth,
                options.exportOptions.sourceHeight,
              );
            },
            text: 'Download PDF document',
          },
        },
        fallbackToExportServer: false,
      },
      navigation: {
        menuStyle: {
          zIndex: 99999,
        },
      },
    };
  }

  protected formatToSeries(data: PieChartType) {
    const rawData = data.values
      .filter(item => Number(item.value) > 0)
      .map(item => ({
        id: !!item.filter?.id ? item.filter.id : `0`,
        name: item.title,
        y: Number(item.value),
        i: item.info || data.title,
        color: Object.values(mapColorGuid).find(
          target => target.guid === item.guid,
        )?.color,
      }));
    return applyMinSliceAngle(rawData);
  }

  protected totalValue(data: PieChartType) {
    return data.values.reduce((acc, item) => acc + Number(item.value), 0);
  }

  public setChartRef(data: HighchartsReact.Props['pie']) {
    this.chartRef = data;
  }

  public resetChartRef() {
    this.chartRef = undefined;
  }

  public resetSelection() {
    this.chartRef?.series[0].points.forEach((point: Highcharts.Point) => {
      point.setState('');
      if (point.sliced) {
        point.sliced = false;
        point.select(false);
      }
    });
  }

  public selectSector(sectorId: string) {
    if (!this.chartRef?.series?.[0]?.points) {
      return;
    }
    const point = this.chartRef.series[0].points.find(
      (p: Highcharts.Point & { id?: string }) =>
        String(p.id) === String(sectorId),
    );
    if (point) {
      point.select(true, false);
    }
  }
}
